import React, { createContext, useContext, useState, ReactNode } from 'react';

export enum TutorialStep {
  NONE = 0,
  SELECT_FILTER = 1,
  SEARCH_INPUT = 2,
  SEARCH_BUTTON = 3,
  SELECT_CHECKBOX = 4,
  ADD_SELECTED_ITEMS = 5,
  SAVE_RULE = 6,
  RULE_NAME_INPUT = 7,
  COMPLETE = 8
}

interface TutorialContextType {
  currentStep: TutorialStep;
  setCurrentStep: (step: TutorialStep) => void;
  startTutorial: () => void;
  nextStep: () => void;
  completeTutorial: () => void;
  isTutorialActive: boolean;
}

const TutorialContext = createContext<TutorialContextType | undefined>(undefined);

export const useTutorial = () => {
  const context = useContext(TutorialContext);
  if (!context) {
    throw new Error('useTutorial must be used within a TutorialProvider');
  }
  return context;
};

interface TutorialProviderProps {
  children: ReactNode;
}

export const TutorialProvider: React.FC<TutorialProviderProps> = ({ children }) => {
  const [currentStep, setCurrentStep] = useState<TutorialStep>(TutorialStep.NONE);
  const [isTutorialActive, setIsTutorialActive] = useState(false);

  const startTutorial = () => {
    setCurrentStep(TutorialStep.SELECT_FILTER);
    setIsTutorialActive(true);
  };

  const nextStep = () => {
    if (currentStep < TutorialStep.COMPLETE) {
      setCurrentStep(currentStep + 1);
    }
  };

  const completeTutorial = () => {
    setCurrentStep(TutorialStep.COMPLETE);
    setTimeout(() => {
      setIsTutorialActive(false);
      setCurrentStep(TutorialStep.NONE);
    }, 5000); // Hide completion message after 5 seconds
  };

  return (
    <TutorialContext.Provider
      value={{
        currentStep,
        setCurrentStep,
        startTutorial,
        nextStep,
        completeTutorial,
        isTutorialActive,
      }}
    >
      {children}
    </TutorialContext.Provider>
  );
};
